import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import {
  FaArrowRight,
  FaBullhorn,
  FaCheckCircle,
  FaClipboardCheck,
  FaComments,
  FaFileAlt,
  FaHeart,
  FaLightbulb,
  FaPlayCircle,
  FaQuoteLeft,
  FaRegStar
} from 'react-icons/fa';
import Breadcrumb from '../components/Breadcrumb.jsx';
import KeywordTopics from '../components/KeywordTopics.jsx';
import { setPageSEO } from '../utils/seo.js';
import { getKeywordCluster } from '../utils/keywordClusters.js';
import './Resources.css';

const resources = [
  {
    slug: 'customer-testimonials',
    title: 'Customer Testimonials',
    eyebrow: 'CUSTOMER TESTIMONIAL SOFTWARE',
    icon: FaQuoteLeft,
    summary: 'Learn how to collect client testimonials that sound credible, specific, and useful to future buyers.',
    intro: 'Strong customer testimonials do more than praise a company. They explain the problem, show the experience, and give buyers evidence that a result is possible.',
    steps: ['Choose customers with a clear outcome', 'Ask about the before-and-after story', 'Collect video, text, or audio responses', 'Publish proof beside the relevant offer'],
    takeaways: ['What makes a testimonial believable', 'How testimonial software simplifies collection', 'Where client testimonials influence decisions']
  },
  {
    slug: 'testimonial-guide',
    title: 'Testimonial Guide',
    eyebrow: 'TESTIMONIAL FORMAT & WRITING',
    icon: FaFileAlt,
    summary: 'Use a practical testimonial format and help customers write clear stories without scripting their praise.',
    intro: 'The best testimonial format is simple: the customer describes their situation, the choice they made, and the meaningful result. Good prompts make that structure feel natural.',
    steps: ['Open with the customer challenge', 'Describe why they chose your solution', 'Add a concrete result or change', 'Keep the customer voice intact'],
    takeaways: ['How to write a testimonial', 'A reusable testimonial format', 'Editing without losing authenticity']
  },
  {
    slug: 'customer-feedback-guide',
    title: 'Customer Feedback Guide',
    eyebrow: 'BETTER CUSTOMER FEEDBACK',
    icon: FaComments,
    summary: 'Ask better client feedback questions, create useful feedback forms, and act on what customers tell you.',
    intro: 'Customer feedback becomes valuable when it is timely, focused, and easy to give. A thoughtful request helps happy customers share positive feedback while revealing what your team can improve.',
    steps: ['Define what you need to learn', 'Ask focused client feedback questions', 'Request feedback at the right moment', 'Close the loop with the customer'],
    takeaways: ['Customer feedback form essentials', 'How to request client feedback', 'Turning positive feedback into proof']
  },
  {
    slug: 'testimonial-marketing',
    title: 'Testimonial Marketing',
    eyebrow: 'TURN PROOF INTO CAMPAIGNS',
    icon: FaBullhorn,
    summary: 'Plan testimonial ads, posts, and campaigns that use customer voices without making them feel manufactured.',
    intro: 'Testimonial marketing works because buyers hear the value from someone who has already made the decision. The right story can strengthen ads, landing pages, emails, and sales conversations.',
    steps: ['Match each story to one buyer concern', 'Choose a format for the channel', 'Lead with the customer outcome', 'Measure engagement and conversion'],
    takeaways: ['Building effective testimonial ads', 'Creating a testimonial post', 'Reusing proof across campaigns']
  },
  {
    slug: 'testimonial-examples',
    title: 'Testimonial Examples',
    eyebrow: 'EXAMPLES THAT INSPIRE ACTION',
    icon: FaRegStar,
    summary: 'Explore short testimonial examples and customer story structures you can adapt to your own collection process.',
    intro: 'Useful testimonial examples reveal why a story works. Specific details, natural language, and a clear result make even short testimonials more persuasive than generic praise.',
    steps: ['Study the story behind the quote', 'Identify the specific outcome', 'Notice the customer language', 'Adapt the structure, not the words'],
    takeaways: ['Short testimonial examples', 'Customer testimonial examples', 'Client testimonial examples by use case']
  },
  {
    slug: 'testimonial-templates',
    title: 'Templates',
    eyebrow: 'READY-TO-USE TEMPLATES',
    icon: FaClipboardCheck,
    summary: 'Start faster with testimonial, feedback form, customer request, and email templates built for real workflows.',
    intro: 'A strong testimonial template removes the blank page without forcing customers into a script. Use clear prompts, friendly request copy, and flexible forms to improve response quality.',
    steps: ['Choose the right request template', 'Customize it for the customer', 'Keep the feedback form focused', 'Follow up politely and clearly'],
    takeaways: ['Customer testimonial templates', 'Feedback form template structure', 'Testimonial request email templates']
  },
  {
    slug: 'how-to-collect-testimonials',
    title: 'Testimonial Collection Guide',
    eyebrow: 'HOW TO COLLECT TESTIMONIALS',
    icon: FaHeart,
    summary: 'Learn when and how to ask for testimonials, which questions to use, and how to build a repeatable collection system.',
    intro: 'The easiest way to collect testimonials is to make the request timely, personal, and simple to complete. A repeatable process turns occasional praise into a dependable proof library.',
    steps: ['Identify the best customer moment', 'Send a personal testimonial request', 'Use questions that invite a story', 'Follow up and ask permission to publish'],
    takeaways: ['How to ask for a testimonial', 'Effective testimonial questions', 'Ways to collect customer testimonials']
  },
  {
    slug: 'reviews-and-testimonials',
    title: 'Reviews & Testimonials',
    eyebrow: 'UNDERSTAND CUSTOMER PROOF',
    icon: FaRegStar,
    summary: 'Understand how reviews and testimonials differ and where each form of customer proof works best.',
    intro: 'Reviews are often short, public, and platform-led. Testimonials are usually collected with permission and shaped around a fuller customer story. Together, they create stronger proof.',
    steps: ['Use reviews for broad public validation', 'Use testimonials for deeper stories', 'Request clear publishing permission', 'Place each format where it matters'],
    takeaways: ['Reviews vs. testimonials', 'Choosing the right proof format', 'Using both across the buyer journey']
  },
  {
    slug: 'social-proof-guide',
    title: 'Social Proof Guide',
    eyebrow: 'MAKE TRUST VISIBLE',
    icon: FaLightbulb,
    summary: 'Explore examples of social proof, choose meaningful social proof metrics, and put trust signals in the right places.',
    intro: 'Social proof reduces uncertainty by showing that other people have chosen, used, and valued your offer. The strongest proof is relevant to the decision a buyer is making now.',
    steps: ['Map moments of buyer hesitation', 'Choose relevant social proof examples', 'Add clear context and attribution', 'Track the metrics that show impact'],
    takeaways: ['Examples of social proof', 'Useful social proof metrics', 'Where to place trust signals']
  },
  {
    slug: 'video-testimonial-guide',
    title: 'Video Testimonials Guide',
    eyebrow: 'AUTHENTIC VIDEO TESTIMONIALS',
    icon: FaPlayCircle,
    summary: 'Plan better questions, help customers record comfortably, and turn raw video responses into credible proof.',
    intro: 'Video testimonials let buyers see and hear a real customer describe their experience. A simple recording flow and thoughtful prompts matter more than a highly produced script.',
    steps: ['Send questions before recording', 'Make mobile recording simple', 'Ask for a concise customer story', 'Edit lightly and publish with context'],
    takeaways: ['Video testimonial examples', 'Questions for video testimonials', 'How to get customer video testimonials']
  }
];

const reveal = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45 } }
};

const stepGuidance = [
  'Define the audience, owner, and intended publishing channel before you create the request. A clear purpose keeps questions focused and makes responses easier to use.',
  'Give customers enough context to answer confidently, but keep the request short. One focused action is more likely to produce a complete response than a long questionnaire.',
  'Review each response for specificity, permission, and relevance. Preserve the customer’s language while removing only repetition or details they did not approve for publication.',
  'Publish the result beside the product, service, or buyer concern it supports. Track where the proof appears and refresh it when the offer or customer context changes.'
];

const commonPitfalls = [
  ['Asking without a clear moment', 'Requests sent without context often produce vague praise. Tie the question to a recent outcome, milestone, purchase, or support experience.'],
  ['Editing away the customer voice', 'Polished proof still needs to sound like the person who gave it. Confirm material edits and keep the original response available for reference.'],
  ['Publishing proof without context', 'A quote is stronger when buyers can understand who said it, what they used, and which result or experience the story supports.']
];

function ResourcesHub() {
  useEffect(() => setPageSEO({
    title: 'Testimonial Resources, Guides & Templates | Clientalio',
    description: 'Explore practical guides, examples, and templates for collecting testimonials, customer feedback, video stories, and social proof.',
    canonical: 'https://clientalio.com/resources',
    ogTitle: 'Resources for Better Testimonials and Customer Proof',
    ogDescription: 'Learn how to collect stronger customer stories and turn them into trusted marketing assets.',
    twitterTitle: 'Clientalio Testimonial Resources',
    twitterDescription: 'Practical guides, examples, and templates for customer testimonials and social proof.'
  }), []);

  return (
    <main className="resources-page">
      <div className="resources-breadcrumb"><div className="container"><Breadcrumb currentLabel="Resources" /></div></div>
      <section className="resources-hero">
        <div className="container resources-hero-grid">
          <motion.div initial="hidden" animate="visible" variants={reveal}>
            <span className="resources-eyebrow">CLIENTALIO RESOURCE LIBRARY</span>
            <h1>Build customer proof people actually trust.</h1>
            <p>Practical guides, examples, and ready-to-use frameworks for collecting better customer stories and putting them to work.</p>
            <a className="resources-primary-action" href="#resource-library">Explore the library <FaArrowRight /></a>
          </motion.div>
          <motion.aside initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.55 }} className="resources-hero-note">
            <span>START HERE</span>
            <h2>A better testimonial starts with a better question.</h2>
            <p>Learn what to ask, when to ask, and how to turn each response into useful proof.</p>
            <Link to="/how-to-collect-testimonials">Read the collection guide <FaArrowRight /></Link>
          </motion.aside>
        </div>
      </section>

      <section className="resources-library" id="resource-library">
        <div className="container">
          <div className="resources-section-heading">
            <span>LEARN BY TOPIC</span>
            <h2>Everything you need to collect and use testimonials</h2>
            <p>Choose a topic and follow a practical path from customer request to published social proof.</p>
          </div>
          <motion.div className="resources-grid" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.08 }}>
            {resources.map((resource, index) => {
              const Icon = resource.icon;
              return (
                <motion.article key={resource.slug} variants={reveal} className="resource-card">
                  <Link to={`/${resource.slug}`}>
                    <div className="resource-card-top"><span>0{index + 1}</span><Icon /></div>
                    <h3>{resource.title}</h3>
                    <p>{resource.summary}</p>
                    <span className="resource-card-action">Read guide <FaArrowRight /></span>
                  </Link>
                </motion.article>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="resources-method">
        <div className="container resources-method-grid">
          <div><span className="resources-eyebrow">A SIMPLE PRACTICE</span><h2>Ask clearly. Collect naturally. Publish responsibly.</h2></div>
          <div className="resources-method-list">
            <p><strong>01</strong><span><b>Start with context.</b> Match every question to the customer moment and story you need.</span></p>
            <p><strong>02</strong><span><b>Remove friction.</b> Let customers respond from any device without creating an account.</span></p>
            <p><strong>03</strong><span><b>Keep it authentic.</b> Review and lightly edit while protecting the customer’s real meaning.</span></p>
          </div>
        </div>
      </section>

      <section className="resources-cta">
        <div className="container resources-cta-inner"><div><span>PUT THE GUIDES INTO PRACTICE</span><h2>Collect your first customer story today.</h2><p>Create a branded request, share one link, and review every response before it goes live.</p></div><Link to="/signup">Start free <FaArrowRight /></Link></div>
      </section>
    </main>
  );
}

function ResourceDetail({ resource }) {
  useEffect(() => setPageSEO({
    title: `${resource.title} | Clientalio Resources`,
    description: resource.summary,
    keywords: getKeywordCluster(resource.slug),
    canonical: `https://clientalio.com/${resource.slug}`,
    ogTitle: `${resource.title} by Clientalio`,
    ogDescription: resource.summary,
    twitterTitle: `${resource.title} | Clientalio`,
    twitterDescription: resource.summary
  }), [resource]);

  const related = resources.filter((item) => item.slug !== resource.slug).slice(0, 3);
  const Icon = resource.icon;

  return (
    <main className="resource-detail-page">
      <div className="resources-breadcrumb"><div className="container"><Breadcrumb items={[{ label: 'Resources', to: '/resources' }]} currentLabel={resource.title} /></div></div>
      <section className="resource-detail-hero">
        <div className="container resource-detail-hero-grid">
          <motion.div initial="hidden" animate="visible" variants={reveal}>
            <span className="resources-eyebrow">{resource.eyebrow}</span>
            <h1>{resource.title}</h1>
            <p>{resource.summary}</p>
            <div className="resource-detail-actions"><a href="#guide">Read the guide <FaArrowRight /></a><Link to="/resources">All resources</Link></div>
          </motion.div>
          <motion.aside initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.55 }} className="resource-detail-summary">
            <Icon />
            <span>IN THIS GUIDE</span>
            <ul>{resource.takeaways.map((takeaway) => <li key={takeaway}><FaCheckCircle /> {takeaway}</li>)}</ul>
          </motion.aside>
        </div>
      </section>

      <KeywordTopics slug={resource.slug} />

      <section className="resource-guide" id="guide">
        <div className="container resource-guide-grid">
          <article>
            <span className="resources-eyebrow">THE ESSENTIALS</span>
            <h2>A practical approach to {resource.title.toLowerCase()}</h2>
            <p>{resource.intro}</p>
            <p>Focus on clarity and relevance. Buyers trust customer proof when it answers a real concern, includes enough context, and sounds like the person who experienced the result.</p>
          </article>
          <aside><h3>Quick checklist</h3>{resource.steps.map((step) => <p key={step}><FaCheckCircle /> {step}</p>)}</aside>
        </div>
      </section>

      <section className="resource-steps-section">
        <div className="container">
          <div className="resources-section-heading"><span>PUT IT INTO PRACTICE</span><h2>Four steps you can use today</h2><p>Keep the process focused enough for your team and easy enough for customers.</p></div>
          <div className="resource-steps-grid">{resource.steps.map((step, index) => <article key={step}><strong>0{index + 1}</strong><h3>{step}</h3><p>{stepGuidance[index]}</p></article>)}</div>
        </div>
      </section>

      <section className="resource-guide">
        <div className="container">
          <div className="resources-section-heading"><span>QUALITY CHECK</span><h2>What good {resource.title.toLowerCase()} looks like</h2><p>Use these criteria to review the process before you publish or reuse customer proof.</p></div>
          <div className="resource-steps-grid">
            {resource.takeaways.map((takeaway, index) => (
              <article key={takeaway}><strong>0{index + 1}</strong><h3>{takeaway}</h3><p>The final material should be specific, easy to understand, connected to a real customer experience, and useful at a defined point in the buyer journey.</p></article>
            ))}
          </div>
        </div>
      </section>

      <section className="resource-steps-section">
        <div className="container">
          <div className="resources-section-heading"><span>AVOID WEAK PROOF</span><h2>Common mistakes to prevent</h2><p>A dependable workflow protects authenticity while making every approved response easier to use.</p></div>
          <div className="resource-steps-grid">
            {commonPitfalls.map(([title, description], index) => <article key={title}><strong>0{index + 1}</strong><h3>{title}</h3><p>{description}</p></article>)}
          </div>
        </div>
      </section>

      <section className="resource-related">
        <div className="container"><div className="resources-section-heading"><span>KEEP LEARNING</span><h2>Related resources</h2></div><div className="resource-related-grid">{related.map((item) => <Link to={`/${item.slug}`} key={item.slug}><span>{item.eyebrow}</span><h3>{item.title}</h3><p>{item.summary}</p><b>Read next <FaArrowRight /></b></Link>)}</div></div>
      </section>

      <section className="resources-cta"><div className="container resources-cta-inner"><div><span>TURN LEARNING INTO CUSTOMER PROOF</span><h2>Make testimonial collection part of your workflow.</h2><p>Give customers a simple way to respond and keep every approved story organized.</p></div><Link to="/signup">Start free <FaArrowRight /></Link></div></section>
    </main>
  );
}

function Resources() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, '').replace(/\/$/, '');
  const resource = resources.find((item) => item.slug === slug);

  return resource ? <ResourceDetail resource={resource} /> : <ResourcesHub />;
}

export { resources };
export default Resources;